/**
 * mymemo 빠른 메모 창 — /?memo-quick=1 (PC 트레이 에이전트의 작은 창, 휴대폰 홈 화면 바로가기).
 * 앱 셸 없이 이 화면만 그린다. 주소의 hsk·text 를 미리 채운다(트레이가 복사한 글에서 세번을 찾아 넘긴다).
 * Ctrl+Enter 저장 · Esc 비우기. 파일은 끌어 놓기·붙여넣기·선택.
 */
import React, { useEffect, useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";
import { ACCEPT, MAX_FILE_BYTES, createMemo, formatHsk, listMemos, memoLoggedIn, uploadMemoAttachment } from "./memoApi";
import "./MymemoPage.css";
import { useLoggedInWatch } from "./QuickClassify";

function params() {
  try { return new URLSearchParams(window.location.search); } catch { return new URLSearchParams(); }
}

export default function MemoQuick() {
  const p = params();
  const [hsk, setHsk] = useState(p.get("hsk") ? formatHsk(p.get("hsk")) : "");
  const [body, setBody] = useState(p.get("text") || "");
  const [tags, setTags] = useState("");
  const [files, setFiles] = useState([]);
  const [state, setState] = useState("");
  const [recent, setRecent] = useState([]);
  const bodyRef = useRef(null);
  const fileRef = useRef(null);
  const loggedIn = useLoggedInWatch(memoLoggedIn);

  useEffect(() => {
    document.title = "mymemo 빠른 메모";
    bodyRef.current?.focus();
    if (loggedIn) listMemos().then((r) => setRecent((r.items || []).slice(0, 5))).catch(() => {});
  }, [loggedIn]);

  const addFiles = (list) => {
    const items = [...list].filter(Boolean);
    const big = items.find((f) => f.size > MAX_FILE_BYTES);
    if (big) { setState(`${big.name}: 25MB 이하만 올릴 수 있습니다.`); return; }
    setFiles((prev) => [...prev, ...items]);
  };

  const save = async () => {
    const code = hsk.replace(/\D/g, "");
    if (!body.trim() && !code && !files.length) { setState("내용을 적어 주세요."); return; }
    setState("저장 중…");
    try {
      const title = (body.split(/\r?\n/).find((l) => l.trim()) || files[0]?.name || "").trim().slice(0, 120) || null;
      const memo = await createMemo({
        hsk: code || null, title, body,
        tags: tags.split(/[,，\s]+/).map((t) => t.trim()).filter(Boolean),
      });
      for (const f of files) await uploadMemoAttachment(memo.id, f);
      setRecent((prev) => [memo, ...prev].slice(0, 5));
      setHsk(""); setBody(""); setTags(""); setFiles([]);
      setState("저장했습니다 · mymemo 에 있습니다");
      bodyRef.current?.focus();
    } catch (e) {
      setState(e.message || "저장하지 못했습니다.");
    }
  };

  const onKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); save(); }
  };

  if (!loggedIn) {
    return (
      <div className="mymemo mm-quick">
        <div className="mm-empty">
          먼저 로그인해 주세요.<br />
          <a href="/" target="_blank" rel="noreferrer">loginHS 열기</a>
        </div>
      </div>
    );
  }

  return (
    <div className="mymemo mm-quick" onKeyDown={onKey}
      onPaste={(e) => { if (e.clipboardData?.files?.length) { e.preventDefault(); addFiles(e.clipboardData.files); } }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
      <div className="mm-fields">
        <input className="mm-f-hsk" value={hsk} onChange={(e) => setHsk(e.target.value)} placeholder="세번(선택)" aria-label="세번" />
        <input className="mm-f-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="태그 (쉼표로)" aria-label="태그" />
      </div>
      <textarea ref={bodyRef} className="mm-body" value={body} onChange={(e) => setBody(e.target.value)}
        placeholder="메모 — 첫 줄이 제목이 됩니다" aria-label="메모" data-testid="quick-body" />
      <div className="mm-files">
        {files.map((f, i) => (
          <div key={`${f.name}-${i}`} className="mm-file">
            <span className="name">{f.name}</span>
            <button type="button" className="mm-icon" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} aria-label="빼기"><X size={13} /></button>
          </div>
        ))}
      </div>
      <div className="mm-quick-bar">
        <button type="button" className="mm-plain" onClick={() => fileRef.current?.click()}><Paperclip size={13} /> 파일</button>
        <input ref={fileRef} type="file" multiple accept={ACCEPT} hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <span className="mm-faint mm-quick-state" role="status">{state}</span>
        <button type="button" className="mm-new" onClick={save} data-testid="quick-save">저장 <span className="mm-key mm-key-dark">Ctrl+Enter</span></button>
      </div>
      {recent.length > 0 && (
        <div className="mm-quick-recent">
          <div className="mm-faint">최근 메모</div>
          {recent.map((m) => (
            <a key={m.id} href="/?audit=mymemo" target="_blank" rel="noreferrer">
              {m.hsk ? <span className="mm-code">{formatHsk(m.hsk)}</span> : null} {m.title || "제목 없음"}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
